import { useState, useRef, useReducer } from 'react';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';

const initialState = {
  user_name: '',
  user_email: '',
  message: '',
  contact_number: '',
};

const initialTouched = {
  user_name: false,
  user_email: false,
  message: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'user_name':
      return { ...state, user_name: action.value };
    case 'user_email':
      return { ...state, user_email: action.value };
    case 'message':
      return { ...state, message: action.value };
    case 'contact_number':
      return { ...state, contact_number: action.value };
    case 'reset':
      return initialState;
    default:
      return state;
  }
}

function getFieldError(field, value) {
  if (!value.trim()) {
    const labels = { user_name: 'Full Name', user_email: 'Email Address', message: 'Message' };
    return `${labels[field]} is required.`;
  }
  if (field === 'user_email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return 'Please enter a valid email address.';
  }
  return '';
}

export const ContactUs = () => {
  const form = useRef();
  const nameRef = useRef();
  const emailRef = useRef();
  const messageRef = useRef();
  const [formState, dispatch] = useReducer(reducer, initialState);
  const [touched, setTouched] = useState(initialTouched);
  const [formSubmitted, setFormSubmitted] = useState({ title: '', paragraph: '' });
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user_name, user_email, message, contact_number } = formState;

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const nameError = touched.user_name ? getFieldError('user_name', user_name) : '';
  const emailError = touched.user_email ? getFieldError('user_email', user_email) : '';
  const messageError = touched.message ? getFieldError('message', message) : '';

  const submitFormAndShowCaptcha = (e) => {
    e.preventDefault();
    setTouched({ user_name: true, user_email: true, message: true });

    const errors = {
      user_name: getFieldError('user_name', user_name),
      user_email: getFieldError('user_email', user_email),
      message: getFieldError('message', message),
    };

    if (errors.user_name) { nameRef.current?.focus(); return; }
    if (errors.user_email) { emailRef.current?.focus(); return; }
    if (errors.message) { messageRef.current?.focus(); return; }

    setShowCaptcha(true);
  };

  const sendEmail = (captchaValue) => {
    if (!captchaValue) return;

    setIsLoading(true);
    const params = {
      ...formState,
      'g-recaptcha-response': captchaValue,
    };

    setFormSubmitted({ title: 'Sending message...', paragraph: '' });

    emailjs.send(
      process.env.REACT_APP_EMAIL_SERVICE_ID,
      process.env.REACT_APP_EMAIL_TEMPLATE_ID,
      params,
      process.env.REACT_APP_EMAIL_PUBLIC_KEY,
    )
      .then(({ status }) => {
        setIsLoading(false);
        if (status === 200) {
          setFormSubmitted({
            title: 'Message sent successfully!',
            paragraph: 'Thank you for contacting us. We will get back to you soon.'
          });
          dispatch({ type: 'reset' });
        } else {
          setFormSubmitted({
            title: 'Unexpected error occurred',
            paragraph: 'Please try again later or contact us directly.'
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error('EmailJS Error:', err);
        setFormSubmitted({
          title: 'Error sending message',
          paragraph: 'Please try again later or contact us directly.'
        });
      });
  };

  if (formSubmitted.title) {
    return (
      <section id="contact" className="contact-section page-section">
        <div className="section-divider"><div></div></div>
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6">
              <div className="modern-card text-center p-5 success-card">
                <div className="success-icon mb-3">
                  {isLoading ? (
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <div className="checkmark-circle">
                      <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                        <circle className="checkmark-circle-path" cx="26" cy="26" r="25" fill="none"/>
                        <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                      </svg>
                    </div>
                  )}
                </div>
                <h3 className="modern-title mb-3">{formSubmitted.title}</h3>
                {formSubmitted.paragraph && (
                  <p className="text-muted">{formSubmitted.paragraph}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="contact-section page-section">
      <div className="section-divider"><div></div></div>
      <div className="container py-5">
        <div className="text-center mb-5">
          <h2 className="modern-title">
            Get In <span className="gradient-text">Touch</span>
          </h2>
          <p className="lead modern-subtitle">
            Ready to start your digital transformation? Let's talk!
          </p>
        </div>

        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="modern-card p-4">
              {!showCaptcha ? (
                <form ref={form} onSubmit={submitFormAndShowCaptcha} noValidate>
                  <div className="mb-3">
                    <label htmlFor="contact-form-name" className="form-label">
                      Full Name <span aria-hidden="true">*</span>
                    </label>
                    <input
                      ref={nameRef}
                      type="text"
                      className={`form-control modern-input ${nameError ? 'is-invalid' : ''}`}
                      id="contact-form-name"
                      name="user_name"
                      value={user_name}
                      onChange={(e) => dispatch({ type: 'user_name', value: e.target.value })}
                      onBlur={() => handleBlur('user_name')}
                      required
                      autoComplete="name"
                      placeholder="Enter your full name"
                      aria-describedby={nameError ? 'name-error' : undefined}
                      aria-invalid={nameError ? 'true' : undefined}
                    />
                    {nameError && (
                      <div id="name-error" className="field-error" role="alert">{nameError}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="contact-form-number" className="form-label">
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      className="form-control modern-input"
                      id="contact-form-number"
                      name="contact_number"
                      value={contact_number}
                      onChange={(e) => dispatch({ type: 'contact_number', value: e.target.value })}
                      autoComplete="tel"
                      placeholder="+40-XXX-XXX-XXX"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="contact-form-email" className="form-label">
                      Email Address <span aria-hidden="true">*</span>
                    </label>
                    <input
                      ref={emailRef}
                      type="email"
                      className={`form-control modern-input ${emailError ? 'is-invalid' : ''}`}
                      id="contact-form-email"
                      name="user_email"
                      value={user_email}
                      onChange={(e) => dispatch({ type: 'user_email', value: e.target.value })}
                      onBlur={() => handleBlur('user_email')}
                      required
                      autoComplete="email"
                      placeholder="name@example.com"
                      aria-describedby={emailError ? 'email-error' : undefined}
                      aria-invalid={emailError ? 'true' : undefined}
                    />
                    {emailError && (
                      <div id="email-error" className="field-error" role="alert">{emailError}</div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-form-message" className="form-label">
                      Message <span aria-hidden="true">*</span>
                    </label>
                    <textarea
                      ref={messageRef}
                      className={`form-control modern-input ${messageError ? 'is-invalid' : ''}`}
                      id="contact-form-message"
                      rows="4"
                      name="message"
                      value={message}
                      onChange={(e) => dispatch({ type: 'message', value: e.target.value })}
                      onBlur={() => handleBlur('message')}
                      required
                      placeholder="Tell us about your project..."
                      aria-describedby={messageError ? 'message-error' : undefined}
                      aria-invalid={messageError ? 'true' : undefined}
                    />
                    {messageError && (
                      <div id="message-error" className="field-error" role="alert">{messageError}</div>
                    )}
                  </div>

                  <button
                    className="btn modern-btn w-100"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              ) : (
                <div className="text-center">
                  <h4 className="mb-3">Please verify you're human</h4>
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                    onChange={sendEmail}
                    theme="light"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;
